import { useEffect, useRef, useState } from "react";
import Header from "./Header";
import LeftRail from "./LeftRail";
import Footer from "./Footer";
import AeroBackground from "./AeroBackground";
import RightDrawer from "../drawer/RightDrawer";
import KpiStrip from "../panes/KpiStrip";
import TasksPane from "../panes/TasksPane";
import NoteEditor, { type NoteEditorHandle } from "../panes/NoteEditor";
import EmbeddedDashboardPane from "../panes/EmbeddedDashboardPane";
import ServicesPane from "../panes/ServicesPane";
import CommandPalette from "./CommandPalette";
import HelpOverlay from "./HelpOverlay";
import { useProjects } from "../../lib/queries";
import { useCockpit } from "../../lib/state";
import { useCockpitKeyboard } from "../../lib/keyboard";
import { projectGradientCss } from "../../lib/projectColor";

export default function Cockpit() {
  const { data: projects = [] } = useProjects();
  const selectedId = useCockpit((s) => s.selectedProjectId);
  const bumpRefresh = useCockpit((s) => s.bumpRefresh);
  const noteRef = useRef<NoteEditorHandle | null>(null);
  const tasksHeaderRef = useRef<HTMLDivElement | null>(null);
  const [showServices, setShowServices] = useState(false);

  const selected = projects.find((p) => p.id === selectedId) ?? projects[0];

  // Bump refresh ts every 30s — Header listens.
  useEffect(() => {
    const t = setInterval(() => bumpRefresh(), 30_000);
    return () => clearInterval(t);
  }, [bumpRefresh]);

  // Hash-driven services view.
  useEffect(() => {
    const handler = () => {
      setShowServices(window.location.hash === "#/services");
    };
    handler();
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  useCockpitKeyboard({
    projects,
    onNewTask: () => {
      const el = document.querySelector<HTMLButtonElement>("[data-tasks-new]");
      el?.click();
    },
    onFocusEditor: () => noteRef.current?.focus(),
  });

  const setSelected = useCockpit((s) => s.setSelectedProjectId);

  return (
    <div className="relative flex h-screen flex-col">
      <AeroBackground />
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <LeftRail />
        <main className="flex flex-1 flex-col overflow-y-auto px-1">
          {showServices ? (
            <ServicesPane />
          ) : selected ? (
            <>
              <ProjectSwitcher
                projects={projects}
                selectedId={selected.id}
                onSelect={(id) => {
                  setSelected(id);
                  window.location.hash = `#/${id}`;
                }}
              />
              <ProjectHeading
                name={selected.name}
                tagline={selected.tagline}
                health={selected.health}
                healthReason={selected.health_reason}
              />
              <KpiStrip />
              <div ref={tasksHeaderRef}>
                <TasksPane projectName={selected.name} />
              </div>
              <NoteEditor ref={noteRef} projectName={selected.name} />
              <EmbeddedDashboardPane projectName={selected.name} />
            </>
          ) : (
            <div className="px-3 py-6 text-xs" style={{ color: "var(--plum)" }}>
              No project selected.
            </div>
          )}
        </main>
        <RightDrawer />
      </div>
      <Footer />
      <CommandPalette
        onNewTask={() => {
          const el = document.querySelector<HTMLButtonElement>("[data-tasks-new]");
          el?.click();
        }}
        onFocusEditor={() => noteRef.current?.focus()}
      />
      <HelpOverlay />
    </div>
  );
}

/// Horizontal pill-bar of every project. Always visible above the project
/// heading so you can switch projects without scrolling the left rail.
function ProjectSwitcher({
  projects,
  selectedId,
  onSelect,
}: {
  projects: Array<{ id: string; name: string }>;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  if (projects.length === 0) return null;
  return (
    <div className="mx-3 mt-3 flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
      {projects.map((p) => {
        const isActive = p.id === selectedId;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            className={isActive ? "glossy-pill glossy-pill--small" : "glossy-pill glossy-pill--small glossy-pill--ghost"}
            style={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {p.name}
          </button>
        );
      })}
    </div>
  );
}

function ProjectHeading({
  name,
  tagline,
  health,
  healthReason,
}: {
  name: string;
  tagline?: string;
  health: string;
  healthReason: string | null;
}) {
  const color =
    health === "green"
      ? "#7ec699"
      : health === "yellow"
        ? "#d4a04c"
        : health === "red"
          ? "#cf6679"
          : "var(--plum)";
  return (
    <div className="aero-glass mx-3 mt-3 px-4 py-3" style={{ borderRadius: 22 }}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span
              className="glossy-radial-bubble"
              style={{ width: 12, height: 12, color }}
            />
            <h2
              className="text-2xl font-semibold leading-none"
              style={{
                fontFamily: "Fraunces, serif",
                fontStyle: "italic",
                color: "var(--ink)",
                letterSpacing: "-0.01em",
              }}
            >
              <span className="holo-text">{name}</span>
            </h2>
            {tagline ? (
              <span
                className="glossy-project-pill"
                style={{ background: projectGradientCss(name, 135) }}
              >
                {tagline}
              </span>
            ) : null}
          </div>
          {healthReason ? (
            <span
              className="mt-1 text-xs"
              style={{
                paddingLeft: 18,
                fontFamily: "Fraunces, serif",
                fontStyle: "italic",
                color: "var(--plum)",
                opacity: 0.85,
              }}
            >
              {healthReason}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
